import { CANONICAL_ORIGIN } from '@/config/site';
import { CITIES } from '@/config/cities';

/**
 * crewLogic.ts — the PURE half of RACER CLUB + FRIENDS (M140; renamed from
 * "Race Crew" in M141). Everything here is framework-free and tested
 * (tests/social/crewLogic.test.ts): the club-name rule (mirrors the server's
 * crew_name_ok FORMAT — the word filter stays server-side), name
 * suggestions, club codes + links, helmet defaults, the club rankings,
 * club-race freshness, the defensive wire parsers, and every line of
 * player-facing status copy (no em dashes — the house rule).
 *
 * NAMING (M141, owner): players only ever read "club" ("Racer Club", "club
 * race", "club code"). The code keeps its M140 internal names (crew*, the
 * crew_* RPCs and tables) — they are contracts players never see.
 *
 * Owner rules this encodes (2026-10-02): ONE club per player, 20 members max,
 * any member may invite, the captain renames / removes / hands over the
 * armband, a leaving captain's armband passes to the longest-serving member.
 * M142 (owner round): helmets are a primary + secondary colour from the
 * 24-colour CLUB PALETTE and one of 7 designs; the club crest is an icon (10,
 * soccer ball by default) in the captain's two palette colours; a club race
 * is a single race (a picked or random city) or a 3/5/9-stop TOUR, club only
 * or open.
 */

export const CREW_MAX = 20;
export const CREW_NAME_MIN = 3;
export const CREW_NAME_MAX = 18;
export const FRIENDS_MAX = 100;
/** The server keeps a club race open 15 minutes; the pop-up stops offering
 *  it a minute earlier so a JOIN never lands on a race that just expired. */
export const CREW_RACE_OFFER_MS = 14 * 60_000;
/** Club races are a 6-car field, like Quick Match (MM_FIELD). */
export const CREW_RACE_FIELD = 6;

// ---- crew names --------------------------------------------------------------

/** Mirrors the SQL format rule exactly: 3-18 characters of letters, digits,
 *  single spaces, ' and -, alphanumeric at both ends, at least 3 letters or
 *  digits. (The word filter is the server's job — crew_name_ok.) */
export function isValidCrewName(name: string): boolean {
  if (!/^[A-Za-z0-9][A-Za-z0-9 '-]{1,16}[A-Za-z0-9]$/.test(name)) return false;
  if (name.includes('  ')) return false;
  return crewNameKey(name).length >= CREW_NAME_MIN;
}

/** The uniqueness key (server: crew_name_key) — "Night Riders" and
 *  "NIGHTRIDERS" are the same crew name. */
export function crewNameKey(name: string): string {
  return name.replace(/[^A-Za-z0-9]/g, '').toLowerCase();
}

/** Tidy what the player typed: collapse runs of spaces, trim, cap length. */
export function tidyCrewName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trimStart().slice(0, CREW_NAME_MAX);
}

// Suggestion vocabulary — the game's arcade + football voice ("FC" = football
// club, M141). Every pair fits 18 characters with its space, and no word can
// trip the name filter.
const CREW_ADJ = [
  'Turbo',
  'Nitro',
  'Night',
  'Golden',
  'Street',
  'Rocket',
  'Thunder',
  'Neon',
  'Wild',
  'Iron',
  'Royal',
  'Rapid',
  'Midnight',
  'Solar',
  'Storm',
  'Velvet',
  'Electric',
  'Atomic',
  'Lucky',
  'Silver',
] as const;
const CREW_NOUN = [
  'Riders',
  'Kickers',
  'Strikers',
  'Wolves',
  'Rockets',
  'Racers',
  'Dribblers',
  'Lions',
  'Falcons',
  'Comets',
  'Hornets',
  'Kings',
  'FC',
  'Squad',
  'Ultras',
  'Drifters',
  'Panthers',
  'Legends',
] as const;

/** One fresh club-name suggestion (format-valid; availability is the server's
 *  call — the create face verifies it and re-rolls on a miss). */
export function suggestCrewName(rand: () => number = Math.random): string {
  for (let i = 0; i < 24; i++) {
    const a = CREW_ADJ[Math.floor(rand() * CREW_ADJ.length) % CREW_ADJ.length];
    const n = CREW_NOUN[Math.floor(rand() * CREW_NOUN.length) % CREW_NOUN.length];
    const name = `${a} ${n}`;
    if (name.length <= CREW_NAME_MAX && isValidCrewName(name)) return name;
  }
  return 'Turbo FC';
}

// ---- codes + links -------------------------------------------------------------

/** A club code off a link / the CLUB CODE box: 6-10 letters or digits (the
 *  server mints 8), case-insensitive. A pasted whole link works too — the
 *  M141 ?club=CODE, and the M140 ?crew=CODE links already out there. null =
 *  it can't be one. */
export function sanitizeCrewCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  const fromLink = /[?&](?:club|crew)=([A-Za-z0-9]+)/.exec(s);
  const v = (fromLink ? fromLink[1] : s).trim().toUpperCase();
  return /^[A-Z0-9]{6,10}$/.test(v) ? v : null;
}

/** The club link (M141: ?club=; the M140 ?crew= links keep working — see
 *  social/links.ts). It carries the SHARER's referral code too, so a
 *  brand-new player who signs up through it also credits the sharer (M101). */
export function crewLink(code: string, refCode?: string | null): string {
  const ref = refCode ? `&ref=${refCode}` : '';
  return `${CANONICAL_ORIGIN}/?club=${code}${ref}`;
}

/** Share copy for a club link — names the code too, so someone who opens the
 *  message inside the app (no link handling) can type it in RACER CLUB. */
export function crewShareText(crewName: string, code: string): string {
  return `Join my Racer Club ${crewName.toUpperCase()} in Dribble Racer! Tap the link, or open RACER CLUB and enter club code ${code}.`;
}

// ---- club colours (helmets + crests) ---------------------------------------------

/**
 * The CLUB PALETTE (M142, owner: "choose primary and secondary colour from a
 * large colour set"). ONE table for helmets AND club crests. The index is the
 * contract (profiles.helmet_primary/secondary, crews.crest_primary/secondary
 * are 0-23 in the database), so entries are only ever APPENDED, never
 * reordered. Names are what the picker's label reads.
 */
export const CLUB_COLORS: readonly { name: string; hex: string }[] = [
  { name: 'RACING RED', hex: '#ef3b2c' }, // 0
  { name: 'CRIMSON', hex: '#a3122a' }, // 1
  { name: 'BLAZE', hex: '#ff7a1a' }, // 2
  { name: 'GOLD', hex: '#f6c331' }, // 3
  { name: 'SUNBURST', hex: '#ffd84a' }, // 4
  { name: 'VOLT', hex: '#b6f02a' }, // 5
  { name: 'PITCH GREEN', hex: '#2fb85a' }, // 6
  { name: 'FOREST', hex: '#17633a' }, // 7
  { name: 'TEAL', hex: '#17c3b2' }, // 8
  { name: 'ICE', hex: '#39e1ff' }, // 9
  { name: 'SKY', hex: '#5ab4ff' }, // 10
  { name: 'ELECTRIC', hex: '#2f8fff' }, // 11
  { name: 'ROYAL', hex: '#2f66ea' }, // 12
  { name: 'NAVY', hex: '#18275f' }, // 13
  { name: 'VIOLET', hex: '#8a4dff' }, // 14
  { name: 'GRAPE', hex: '#5a2a96' }, // 15
  { name: 'HOT PINK', hex: '#ff46a8' }, // 16
  { name: 'ROSE', hex: '#ff9cc2' }, // 17
  { name: 'COPPER', hex: '#c9773a' }, // 18
  { name: 'WHITE', hex: '#f1f5fa' }, // 19
  { name: 'SILVER', hex: '#b4bfcc' }, // 20
  { name: 'GUNMETAL', hex: '#4a515d' }, // 21
  { name: 'STEALTH', hex: '#2a2e37' }, // 22
  { name: 'BLACK', hex: '#121419' }, // 23
];
export const CLUB_COLOR_COUNT = CLUB_COLORS.length;

/** A palette colour's hex (an out-of-range index reads as GOLD, never blank). */
export function clubHex(i: number): string {
  return (CLUB_COLORS[i] ?? CLUB_COLORS[3]).hex;
}

export function clubColorName(i: number): string {
  return (CLUB_COLORS[i] ?? CLUB_COLORS[3]).name;
}

// ---- helmets ---------------------------------------------------------------------

/** M140 shipped 7 fixed COLOURWAYS (helmet_color 0-6). Clients on build 21 /
 *  dr-v136 still send and read them, so the server keeps that column in step
 *  and every new client can read an M140-only row. */
export const LEGACY_HELMET_COLORS = 7;
export const HELMET_DESIGNS = 7;

/** An M140 colourway → its primary/secondary pair in CLUB_COLORS (INFERNO,
 *  CHAMPION, GLACIER, VOLT, ROYAL, NEON, STEALTH). Mirrored in SQL
 *  (m142_club_style dr_legacy_pair) — a test pins the two. */
export const LEGACY_HELMET_PAIRS: readonly (readonly [number, number])[] = [
  [0, 23],
  [3, 23],
  [19, 11],
  [5, 23],
  [12, 4],
  [16, 9],
  [22, 0],
];

/** A palette colour → the nearest M140 colourway, which the server writes into
 *  helmet_color so an old client still draws something close (SQL mirror:
 *  dr_legacy_color). */
export const LEGACY_COLOR_OF: readonly number[] = [
  0, 0, 0, 1, 1, 3, 3, 3, 3, 2, 4, 4, 4, 4, 5, 5, 5, 5, 0, 2, 2, 6, 6, 6,
];

export interface Helmet {
  /** The shell colour (CLUB_COLORS index). */
  primary: number;
  /** The design's colour (CLUB_COLORS index). */
  secondary: number;
  design: number;
}

/** FNV-1a — a stable small hash of a player id. */
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** The helmet a player wears before choosing one: derived from their id, so
 *  an untouched roster still reads as different racers. Built from the M140
 *  rule (colourway = hash % 7) so old and new clients draw the same helmet. */
export function defaultHelmet(userId: string): Helmet {
  const h = hash32(userId || 'racer');
  const [primary, secondary] = LEGACY_HELMET_PAIRS[h % LEGACY_HELMET_COLORS];
  return { primary, secondary, design: (h >>> 8) % HELMET_DESIGNS };
}

function inRange(v: unknown, n: number): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < n;
}

export function isValidHelmet(h: Helmet): boolean {
  return (
    inRange(h.primary, CLUB_COLOR_COUNT) &&
    inRange(h.secondary, CLUB_COLOR_COUNT) &&
    inRange(h.design, HELMET_DESIGNS)
  );
}

/**
 * The helmet to draw off a wire row: the M142 primary/secondary pair when the
 * server has one, else the M140 colourway mapped onto the palette, else the
 * id-derived default. `prefix` reads e.g. starter_helmet_primary.
 */
export function helmetFromRow(row: unknown, userId: string, prefix = 'helmet'): Helmet {
  const o = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const design = o[`${prefix}_design`];
  const primary = o[`${prefix}_primary`];
  const secondary = o[`${prefix}_secondary`];
  const color = o[`${prefix}_color`];
  if (!inRange(design, HELMET_DESIGNS)) return defaultHelmet(userId);
  if (inRange(primary, CLUB_COLOR_COUNT) && inRange(secondary, CLUB_COLOR_COUNT)) {
    return { primary, secondary, design };
  }
  if (inRange(color, LEGACY_HELMET_COLORS)) {
    const [p, s] = LEGACY_HELMET_PAIRS[color];
    return { primary: p, secondary: s, design };
  }
  return defaultHelmet(userId);
}

// ---- club crests -----------------------------------------------------------------

/** The crest icons (M142, owner-approved set of 10). Index = crews.crest_icon. */
export const CREST_ICON_NAMES: readonly string[] = [
  'SOCCER BALL',
  'CROWN',
  'LIGHTNING',
  'FLAME',
  'TROPHY',
  'WINGS',
  'CHECKERED FLAG',
  'RACE CAR',
  'DRAGON',
  'LION',
];
export const CREST_ICONS = CREST_ICON_NAMES.length;

export interface Crest {
  icon: number;
  /** The shield's field (CLUB_COLORS index). */
  primary: number;
  /** The rim + the icon (CLUB_COLORS index). */
  secondary: number;
}

/** No pick = the soccer ball on the house look (a dark field, gold rim) —
 *  every M140/M141 club wears this until its captain picks. */
export const DEFAULT_CREST: Crest = { icon: 0, primary: 22, secondary: 3 };

export function isValidCrest(c: Crest): boolean {
  return (
    inRange(c.icon, CREST_ICONS) &&
    inRange(c.primary, CLUB_COLOR_COUNT) &&
    inRange(c.secondary, CLUB_COLOR_COUNT)
  );
}

/** A crest off a wire row (crest_icon / crest_primary / crest_secondary). */
export function crestFromRow(row: unknown, prefix = 'crest'): Crest {
  const o = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const c = {
    icon: o[`${prefix}_icon`] as number,
    primary: o[`${prefix}_primary`] as number,
    secondary: o[`${prefix}_secondary`] as number,
  };
  return isValidCrest(c) ? c : { ...DEFAULT_CREST };
}

// ---- wire types + parsers ----------------------------------------------------------

export type CrewRole = 'captain' | 'member';

export interface CrewInfo {
  id: string;
  name: string;
  code: string;
  createdAt: number;
  /** Crew races ever started. */
  races: number;
  myRole: CrewRole;
  max: number;
  /** The captain's pick (M142); DEFAULT_CREST until one is made. */
  crest: Crest;
}

export interface CrewMember {
  userId: string;
  name: string;
  role: CrewRole;
  joinedAt: number;
  helmet: Helmet;
  /** Career XP. */
  xp: number;
  /** XP this ET week (the weekly leaderboard's clock). */
  week: number;
  /** Single-race victories, podiums, tour titles (the trophy cabinet's counts). */
  wins: number;
  podiums: number;
  titles: number;
  friend: boolean;
  me: boolean;
}

export interface CrewInviteRow {
  crewId: string;
  name: string;
  members: number;
  inviter: string;
  at: number;
  crest: Crest;
}

export interface CrewRaceInvite {
  id: string;
  crewId: string;
  crewName: string;
  startedBy: string;
  starterName: string;
  starterHelmet: Helmet;
  visibility: 'private' | 'public';
  room: string;
  host: string;
  city: string;
  field: number;
  /** M142: 0 = a single race in `city`; 3-9 = a club TOUR of that many stops
   *  (the room rolls the route; `city` is then unused). */
  tourLen: number;
  createdAt: number;
  closed: boolean;
}

export interface CrewOverview {
  crew: CrewInfo | null;
  members: CrewMember[];
  /** Invites this crew has out (by Racer ID). */
  sent: { userId: string; name: string }[];
  /** The crew's open race, if any. */
  race: CrewRaceInvite | null;
  /** Invites waiting for ME (from other crews). */
  invites: CrewInviteRow[];
}

export interface FriendRow {
  userId: string;
  name: string;
  helmet: Helmet;
  xp: number;
  crewId: string | null;
  crewName: string;
  sameCrew: boolean;
  invited: boolean;
}

export interface FriendRequestRow {
  userId: string;
  name: string;
  helmet: Helmet;
  xp: number;
  at: number;
}

export interface FriendsOverview {
  friends: FriendRow[];
  incoming: FriendRequestRow[];
  outgoing: { userId: string; name: string; at: number }[];
}

export interface SocialInbox {
  race: CrewRaceInvite | null;
  inCrew: boolean;
  crewInvites: number;
  friendRequests: number;
}

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === 'object' ? (v as Obj) : {});
const str = (v: unknown, d = ''): string => (typeof v === 'string' ? v : d);
const num = (v: unknown, d = 0): number => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : d;
};
const time = (v: unknown): number => {
  const t = typeof v === 'string' ? Date.parse(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(t) ? t : 0;
};
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

/** A crew race off the wire (the server's dr_crew_race_json). null = not one. */
export function parseCrewRace(v: unknown): CrewRaceInvite | null {
  const o = obj(v);
  const id = str(o.id);
  // M142: a club TOUR's room travels as `tour_room` ('room' is null for it),
  // so M140/M141 clients, which know nothing of tours, never pick one up.
  const room = str(o.room) || str(o.tour_room);
  if (!id || !/^[A-Z0-9]{4,8}$/.test(room)) return null;
  const startedBy = str(o.started_by);
  const city = str(o.city);
  return {
    id,
    crewId: str(o.crew_id),
    crewName: str(o.crew_name),
    startedBy,
    starterName: str(o.starter_name),
    starterHelmet: helmetFromRow(o, startedBy, 'starter_helmet'),
    visibility: o.visibility === 'public' ? 'public' : 'private',
    room,
    host: str(o.host, 'us'),
    city: CITIES.some((c) => c.id === city) ? city : CITIES[0].id,
    field: Math.min(7, Math.max(2, Math.round(num(o.field, CREW_RACE_FIELD)))),
    tourLen: sanitizeCrewTourLen(o.tour_len),
    createdAt: time(o.created_at),
    closed: o.closed_at !== null && o.closed_at !== undefined,
  };
}

export function parseCrewOverview(v: unknown): CrewOverview {
  const o = obj(v);
  const c = o.crew ? obj(o.crew) : null;
  const crew: CrewInfo | null =
    c && str(c.id)
      ? {
          id: str(c.id),
          name: str(c.name),
          code: str(c.code),
          createdAt: time(c.created_at),
          races: num(c.races),
          myRole: c.my_role === 'captain' ? 'captain' : 'member',
          max: num(c.max, CREW_MAX),
          crest: crestFromRow(c),
        }
      : null;
  const members: CrewMember[] = list(o.members).map((m) => {
    const x = obj(m);
    const userId = str(x.user_id);
    return {
      userId,
      name: str(x.name, 'RACER'),
      role: x.role === 'captain' ? 'captain' : 'member',
      joinedAt: time(x.joined_at),
      helmet: helmetFromRow(x, userId),
      xp: num(x.xp),
      week: num(x.week),
      wins: num(x.wins),
      podiums: num(x.podiums),
      titles: num(x.titles),
      friend: x.friend === true,
      me: x.me === true,
    };
  });
  return {
    crew,
    members: crew ? members : [],
    sent: list(o.sent).map((s) => ({ userId: str(obj(s).user_id), name: str(obj(s).name) })),
    race: parseCrewRace(o.race),
    invites: list(o.invites).map((i) => {
      const x = obj(i);
      return {
        crewId: str(x.crew_id),
        name: str(x.name),
        members: num(x.members),
        inviter: str(x.inviter),
        at: time(x.at),
        crest: crestFromRow(x),
      };
    }),
  };
}

export function parseFriendsOverview(v: unknown): FriendsOverview {
  const o = obj(v);
  return {
    friends: list(o.friends).map((f) => {
      const x = obj(f);
      const userId = str(x.user_id);
      return {
        userId,
        name: str(x.name, 'RACER'),
        helmet: helmetFromRow(x, userId),
        xp: num(x.xp),
        crewId: str(x.crew_id) || null,
        crewName: str(x.crew_name),
        sameCrew: x.same_crew === true,
        invited: x.invited === true,
      };
    }),
    incoming: list(o.incoming).map((f) => {
      const x = obj(f);
      const userId = str(x.user_id);
      return {
        userId,
        name: str(x.name, 'RACER'),
        helmet: helmetFromRow(x, userId),
        xp: num(x.xp),
        at: time(x.at),
      };
    }),
    outgoing: list(o.outgoing).map((f) => {
      const x = obj(f);
      return { userId: str(x.user_id), name: str(x.name, 'RACER'), at: time(x.at) };
    }),
  };
}

export function parseSocialInbox(v: unknown): SocialInbox {
  const o = obj(v);
  return {
    race: parseCrewRace(o.race),
    inCrew: o.in_crew === true,
    crewInvites: num(o.crew_invites),
    friendRequests: num(o.friend_requests),
  };
}

// ---- rankings --------------------------------------------------------------------

export type CrewSort = 'week' | 'xp' | 'wins';

export const CREW_SORTS: readonly { id: CrewSort; label: string; unit: string }[] = [
  { id: 'week', label: 'THIS WEEK', unit: 'XP' },
  { id: 'xp', label: 'ALL TIME', unit: 'XP' },
  { id: 'wins', label: 'WINS', unit: 'WINS' },
];

/** The value a member is ranked by under `sort`. */
export function rankValue(m: CrewMember, sort: CrewSort): number {
  return sort === 'week' ? m.week : sort === 'xp' ? m.xp : m.wins;
}

/**
 * The crew rankings: best first, with fixed tie-breaks so the order never
 * jitters between refreshes (week → career XP; career XP → this week; wins →
 * podiums → career XP), then the longest-serving member, then the id.
 */
export function rankMembers(members: readonly CrewMember[], sort: CrewSort): CrewMember[] {
  const keys = (m: CrewMember): number[] =>
    sort === 'week' ? [m.week, m.xp] : sort === 'xp' ? [m.xp, m.week] : [m.wins, m.podiums, m.xp];
  return [...members].sort((a, b) => {
    const ka = keys(a);
    const kb = keys(b);
    for (let i = 0; i < ka.length; i++) if (ka[i] !== kb[i]) return kb[i] - ka[i];
    if (a.joinedAt !== b.joinedAt) return a.joinedAt - b.joinedAt;
    return a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0;
  });
}

/** The crew's headline numbers (the stat tiles). */
export function crewTotals(members: readonly CrewMember[]): {
  xp: number;
  week: number;
  wins: number;
} {
  let xp = 0;
  let week = 0;
  let wins = 0;
  for (const m of members) {
    xp += m.xp;
    week += m.week;
    wins += m.wins;
  }
  return { xp, week, wins };
}

// ---- crew races ------------------------------------------------------------------

/** Still worth offering (open, and inside the offer window)? */
export function isCrewRaceFresh(race: CrewRaceInvite | null, now: number = Date.now()): boolean {
  if (!race || race.closed || race.createdAt <= 0) return false;
  const age = now - race.createdAt;
  return age >= -60_000 && age < CREW_RACE_OFFER_MS;
}

/** The city a crew race uses when nobody picks one: random, like Quick Match. */
export function randomCrewCity(rand: () => number = Math.random): string {
  return CITIES[Math.floor(rand() * CITIES.length) % CITIES.length].id;
}

/** M142 (owner): a club race can be a TOUR — the same stop counts Challenge
 *  Friends offers. 5 leads (it is what START A TOUR searchers expect). */
/**
 * What this app tells the server it understands when it registers its push
 * token (push_tokens.caps, M142 review). 1 = club tours: the race JSON's
 * `tour_room` and `tour_len`. crew-push sends a club TOUR ping only to tokens
 * registered with caps >= 1, so a phone still on an older build is never
 * pinged with a tour it cannot open. Raise it when a future ping needs a
 * newer app; never lower it.
 */
export const PUSH_CAPS = 1;

export const CREW_TOUR_LENGTHS: readonly number[] = [3, 5, 9];
export const CREW_TOUR_DEFAULT = 5;

/** A tour length off the wire: 3-9 stops, anything else = a single race (0).
 *  Mirrors the crew_races.tour_len check. */
export function sanitizeCrewTourLen(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isInteger(n) && n >= 3 && n <= 9 ? n : 0;
}

/** What the starter picks on the CALL YOUR CLUB sheet (M142, owner order:
 *  quick race or tour → the city or the stops → club only or open). */
export interface CrewRaceChoice {
  visibility: 'private' | 'public';
  /** 0 = a single race; 3 / 5 / 9 = a tour. */
  tour: number;
  /** A single race's city, or 'random'. Ignored for a tour. */
  city: string;
}

/** The race the starter's choice becomes (a picked city, else a random one). */
export function resolveCrewRaceChoice(
  c: CrewRaceChoice,
  rand: () => number = Math.random,
): { visibility: 'private' | 'public'; tour: number; city: string } {
  const tour = sanitizeCrewTourLen(c.tour);
  const city = CITIES.some((x) => x.id === c.city) ? c.city : randomCrewCity(rand);
  return { visibility: c.visibility === 'public' ? 'public' : 'private', tour, city };
}

/** "5-STOP TOUR" / "TOKYO" — what a club race IS, for the invite pop-up, the
 *  JOIN CLUB RACE button and the lobby line. */
export function crewRaceWhat(race: Pick<CrewRaceInvite, 'tourLen' | 'city'>): string {
  if (race.tourLen > 0) return `${race.tourLen}-STOP TOUR`;
  const city = CITIES.find((c) => c.id === race.city);
  return (city ? city.name : race.city).toUpperCase();
}

// ---- status copy (plain English, no em dashes) ------------------------------------

export function createCrewMessage(status: string): string {
  switch (status) {
    case 'ok':
      return '';
    case 'taken':
      return 'That club name is taken. Try another.';
    case 'invalid':
      return "That name isn't allowed. Use 3 to 18 letters, numbers or spaces.";
    case 'in_crew':
      return "You're already in a club. Leave it first to start a new one.";
    case 'rate':
      return "That's a lot of new clubs today. Try again tomorrow.";
    default:
      return 'Could not create the club. Check your connection.';
  }
}

export function inviteMessage(status: string, name: string): string {
  const who = name ? name.toUpperCase() : 'THEM';
  switch (status) {
    case 'sent':
      return `INVITE SENT TO ${who}`;
    case 'pending':
      return `${who} ALREADY HAS YOUR INVITE`;
    case 'already_member':
      return `${who} IS ALREADY IN YOUR CLUB`;
    case 'not_found':
      return 'NO RACER WITH THAT RACER ID';
    case 'self':
      return "THAT'S YOU!";
    case 'full':
      return `YOUR CLUB IS FULL (${CREW_MAX}/${CREW_MAX})`;
    case 'limit':
      return 'TOO MANY INVITES WAITING. TRY AGAIN LATER';
    case 'not_in_crew':
      return 'JOIN OR START A CLUB FIRST';
    case 'rate':
      return 'SLOW DOWN A LITTLE. TRY AGAIN IN A WHILE';
    default:
      return 'COULD NOT SEND. CHECK YOUR CONNECTION';
  }
}

export function friendRequestMessage(status: string, name: string): string {
  const who = name ? name.toUpperCase() : 'THEM';
  switch (status) {
    case 'sent':
      return `FRIEND REQUEST SENT TO ${who}`;
    case 'accepted':
    case 'added':
      return `YOU AND ${who} ARE NOW FRIENDS`;
    case 'already':
      return `YOU'RE ALREADY FRIENDS WITH ${who}`;
    case 'pending':
      return `REQUEST ALREADY SENT TO ${who}`;
    case 'not_found':
      return 'NO RACER WITH THAT RACER ID';
    case 'self':
      return "THAT'S YOU!";
    case 'full':
      return `FRIEND LIST IS FULL (${FRIENDS_MAX})`;
    case 'limit':
      return 'TOO MANY REQUESTS WAITING. TRY AGAIN LATER';
    case 'rate':
      return 'SLOW DOWN A LITTLE. TRY AGAIN IN A WHILE';
    default:
      return 'COULD NOT SEND. CHECK YOUR CONNECTION';
  }
}

export function joinCrewMessage(status: string): string {
  switch (status) {
    case 'ok':
      return '';
    case 'full':
      return `That club is full (${CREW_MAX}/${CREW_MAX}).`;
    case 'not_found':
    case 'invalid':
      return "That club link doesn't work anymore. Ask for a new one.";
    case 'gone':
      return 'That invite has expired.';
    case 'in_crew':
      return "You're already in a club.";
    case 'rate':
      return 'Too many club changes for now. Try again in a while.';
    default:
      return 'Could not join. Check your connection.';
  }
}
